<script setup>
import { defineProps, onMounted, ref } from "vue";
import { fetchLogs } from "@/services/api/apiServiceMessages";
import { useToast } from "vue-toastification";

const props = defineProps({
  phone: {
    type: String,
    required: true,
  },
});

const toast = useToast();
const logs = ref(null);
const isLoading = ref(false);

// Function to fetch logs and order them from newest to oldest
const loadLogs = async () => {
  try {
    isLoading.value = true;
    const res = await fetchLogs({
      page: 1,
      phone: props.phone,
    });

    // Sort logs by created_at in descending order (newest first)
    const sortedLogs = res.data.data.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA; // Descending order
    });

    return sortedLogs;
  } catch (err) {
    console.error(err);
    toast.error("مشکلی در بارگزاری پیام های ارسالی بوجود آمد.");
  } finally {
    isLoading.value = false;
  }
};

function formatDateFa(dateStr) {
  const dt = new Date(dateStr.replace(" ", "T"));
  if (isNaN(dt)) return dateStr;
  return dt.toLocaleString("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

onMounted(async () => {
  logs.value = await loadLogs();
});
</script>

<template>
  <div class="overflow-x-auto">
    <table class="min-w-full text-sm">
      <thead class="bg-gray-50 text-gray-600">
        <tr>
          <th class="px-4 py-2 text-right font-semibold whitespace-nowrap">
            آیدی
          </th>
          <th class="px-4 py-2 text-right font-semibold whitespace-nowrap">
            متن پیام
          </th>
          <th class="px-4 py-2 text-right font-semibold whitespace-nowrap">
            تاریخ ایجاد
          </th>
        </tr>
      </thead>

      <!-- Loading skeleton -->
      <tbody v-if="isLoading">
        <tr class="animate-pulse">
          <td class="px-4 py-3">
            <div class="h-4 bg-gray-200 rounded w-full"></div>
          </td>
          <td class="px-4 py-3">
            <div class="h-4 bg-gray-200 rounded w-full"></div>
          </td>
          <td class="px-4 py-3">
            <div class="h-4 bg-gray-200 rounded w-full"></div>
          </td>
        </tr>
      </tbody>

      <!-- Data rows -->
      <tbody v-else>
        <tr
          v-for="msg in logs"
          :key="msg.id"
          class="border-t border-gray-100 hover:bg-gray-50"
        >
          <td class="px-4 py-3 text-gray-900">{{ msg.id }}</td>
          <td class="px-4 py-3 text-gray-700">
            <div class="line-clamp-3">{{ msg.message_content }}</div>
          </td>
          <td class="px-4 py-3 text-gray-700 whitespace-nowrap">
            {{ formatDateFa(msg.created_at) }}
          </td>
        </tr>
      </tbody>

      <!-- Empty state -->
      <tbody v-if="false">
        <tr>
          <td colspan="5" class="px-4 py-10 text-center text-gray-500">
            داده‌ای برای نمایش موجود نیست.
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
